// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import World from '../../entities/World';
import SkeletonJointIndexMap from '../../animationpack/SkeletonJointIndexMap';

/**
 * Maintains state information about an instance of a specific animation clip, such as time scaling applied, active flag, start time of the
 *        instance, etc.
 */
class AnimationClipInstance {

  /**
   * @param {World} [world] - the sumerian world this clip belongs to
   */
  constructor(world) {
    if (world === undefined) {
      console.warn('[Sumerian] AnimationClipInstance object constructed without a world object. Playback may be erratic.');
    }
    this._world = world;
    this._active = true;
    this._loopCount = 0;
    this._timeScale = 1.0;
    this._startTime = 0.0;
    this._prevClockTime = 0.0;
    this._prevUnscaledClockTime = 0.0;
    this._clipStateObjects = {};
    this._skeletonJointIndexMap = null; // maps joint paths to a joint index for the setSkeleton() skeleton
  }

  /**
   * Sets the timescale of the animation, speeding it up or slowing it down
   * @param {number} scale
   * @param {number} [globalTime=this._world.time]
   */
  setTimeScale(scale, globalTime) {
    globalTime = typeof globalTime !== 'undefined' ? globalTime : (this._world ? this._world.time : 0);
    if (this._active && this._timeScale !== scale) {
      if (this._timeScale !== 0.0 && scale !== 0.0) {
        // move startTime to account for change in scale
        var now = globalTime;
        let timePassed = now - this._startTime;
        timePassed *= this._timeScale;
        timePassed /= scale;
        this._startTime = now - timePassed;
      } else if (this._timeScale === 0.0) {
        var now = globalTime;
        this._startTime = now - this._prevUnscaledClockTime;
      }
    }
    this._timeScale = scale;
  }

  /**
   * Links this AnimationClipInstance to the specified Skeleton.
   *
   * This *must* be called when the clip instance is assigned to an animState
   * (i.e. linked to an layer and animation component). Failure to do so will
   * cause the clip not to animate the skeleton as Joint Paths will not be mapped
   * (or mapped incorrectly) to skeleton indices without a call to setSkeleton()
   *
   * @param {Object} skeleton {@link Skeleton} skeleton to use for join path to index mapping.
   */
  setSkeleton(skeleton) {
    this._skeletonJointIndexMap = new SkeletonJointIndexMap(skeleton);
    this._clipStateObjects = {};
  }

  /**
   * Gives the corresponding data for a channel, to apply animations to
   * @param {AbstractAnimationChannel} channel
   * @returns {(TransformData|TriggerData|Array<number>)} the animation data item
   */
  getApplyTo(channel) {
    const channelName = channel._channelName;
    const cachedChannelData = this._clipStateObjects[channelName];

    if (cachedChannelData) {
      return cachedChannelData;
    }

    const channelData = channel.createStateDataObject();
    let jointIndex = null;

    if (this._skeletonJointIndexMap) {
      // First attempt to find a match on the full channel name. If that fails, remove
      // any namespace prefix and try again.
      jointIndex = this._skeletonJointIndexMap.getJointIndex(channelName);

      if (jointIndex === null && channelName.includes(':')) {
        // Split the path into elements, split each element by the namespace delimiter,
        // then join the elements on '|' to re-create the path.
        const jointPathWithoutNamespaces = channelName.split('|')
          .map(pathElement => pathElement.split(':').slice(-1))
          .join('|');

        jointIndex = this._skeletonJointIndexMap.getJointIndex(jointPathWithoutNamespaces);
      }
    }

    if (jointIndex === null) {
      console.log(`[Sumerian] info: Could not match Joint Channel path ${channel._jointName} to a joint in the skeleton, channel will be ignored.`);
    } else {
      channelData._jointIndex = jointIndex;
    }

    this._clipStateObjects[channelName] = channelData;
    return channelData;
  }

  clone() {
    const cloned = new AnimationClipInstance(this._world);

    cloned._active = this._active;
    cloned._loopCount = this._loopCount;
    cloned._timeScale = this._timeScale;
    cloned._skeletonJointIndexMap = this._skeletonJointIndexMap;

    return cloned;
  }
}

export default AnimationClipInstance;
